<script setup lang="ts">
import usePoolStore from '~~/app/store/pool'
import PoolDoesNotHavePicture from '../Common/PoolDoesNotHavePicture.vue'
import { getPhotoUrlBySize } from '#cmui/utils'
import { PhotoSizes } from '#cmui/types'
import type { PoolPhoto } from '#cmui/types'

const props = defineProps({
    isContainedInDrawer: {
        type: Boolean,
        default: true
    },
    isExploitation: {
        type: Boolean,
        required: true
    }
})

const emits = defineEmits([
    'pictureIndex',
    'showPicturePopUp',
    'sendPictures',
    'showUserNotConnectedModal'
])

const getPictures = computed<PoolPhoto[]>(() => usePoolStore().poolPhotos || [])

const userId = computed<string>(() => useUserSession().user?.value?.id || '')

const openPicture = (index: number) => {
    if (userId.value || props.isExploitation) {
        emits('sendPictures', usePoolStore().poolPhotos)
        emits('pictureIndex', index)
        emits('showPicturePopUp')
    } else if (index === 0) {
        emits('sendPictures', usePoolStore().poolPhotos?.[0])
        emits('pictureIndex', 0)
        emits('showPicturePopUp')
    } else {
        emits('showUserNotConnectedModal')
    }
}

const redirectOnAddPictures = () => {
    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            { action: 'openUrl', url: `wizard/${usePoolStore().poolId}#6` },
            document.referrer
        )
    } else {
        return navigateTo(
            getChargemapUrl(`/wizard/${usePoolStore().poolId}#6`),
            { external: true }
        )
    }
}

onMounted(() => {
    usePoolStore().fetchPoolPhotos()
})
</script>

<template>
    <div class="h-full">
        <Loader v-if="usePoolStore().communityPoolPhotosLoader" />
        <PoolDoesNotHavePicture
            v-else-if="!usePoolStore().poolPhotos.length"
            :is-contained-in-drawer
        />
        <div v-else class="text-sm p-5 flex flex-col">
            <div v-if="userId" class="mb-3.5 mx-auto w-fit">
                <ChargemapB2CButton
                    type="primary"
                    size="large"
                    @click="redirectOnAddPictures"
                >
                    <template #icon>
                        <IconCamera class="size-5" />
                    </template>
                    {{ $t('add_photos') }}
                </ChargemapB2CButton>
            </div>

            <div class="flex flex-wrap">
                <div
                    v-for="(photo, index) of getPictures"
                    :key="photo.id"
                    class="overflow-hidden rounded-lg h-full w-1/2 425:w-1/3 sm:w-1/4 p-1"
                    :class="{
                        'justify-start': !isContainedInDrawer
                    }"
                >
                    <div
                        class="relative left-0 h-full flex items-center justify-center cursor-pointer overflow-hidden rounded-lg"
                        @click="openPicture(index)"
                    >
                        <NuxtImg
                            :id="String(photo.id)"
                            width="160"
                            height="160"
                            class="rounded-lg transform transition-transform rotate-0 aspect-square object-cover"
                            loading="lazy"
                            :src="getPhotoUrlBySize(photo, PhotoSizes.SMALL)"
                            :class="{
                                'filter blur-sm bg-opacity-10 overflow-hidden':
                                    index !== 0 && !userId && !isExploitation
                            }"
                            :alt="usePoolStore().poolName"
                        />
                        <IconEye
                            v-if="index !== 0 && !userId && !isExploitation"
                            class="absolute text-white size-6"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
